import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import "./pages.css";
import { loginUser, loginWithToken, verifyLoginOtp, clear2FA } from "./loginstate";
import { createTheme } from "@material-ui/core/styles";
import {
  Grid,
  Container,
  TextField,
  ThemeProvider,
  Box,
  Typography,
  Button,
} from "@material-ui/core";
import { Redirect } from "react-router-dom";
import Toast from "../../commons/Toast/Toast";
import login from "../../static/login.jpg";
import ReCAPTCHA from "react-google-recaptcha";
import { GOOGLE_PUBLIC_KEY, TENANT_ID, URL as url } from "../../config/config";

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userName: "",
      password: "",
      otp: "",
      status: "",
      statusMessage: "",
      showSnackbar: false,
      alertMsg: "",
      alertSeverity: "",
      recaptchaToken: null,
      // forgot password flow
      // forgotStep: 0 = login form (default)
      // forgotStep: 1 = send OTP screen (enter username + captcha)
      // forgotStep: 2 = verify OTP screen (enter 6-digit OTP)
      // forgotStep: 3 = reset password screen (new + confirm password)
      forgotStep: 0,
      forgotUsername: "",
      forgotMaskedEmail: "",   // masked email returned by send-otp API e.g. "n***@gmail.com"
      forgotMaskedPhone: "",   // masked phone returned by send-otp API
      forgotOtp: "",
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      confirmPasswordError: "",
      forgotLoading: false,
      forgotRecaptchaToken: null,  // captcha token for send-otp step
      resendRecaptchaToken: null,  // captcha token for resend (step 2, multi-tenant only)
      resetToken: "",              // short-lived JWT from verify-otp, consumed by /reset
      resendCountdown: 0,          // seconds remaining before resend OTP is enabled
      resendAttemptsRemaining: null, // null = unknown; 0 = daily limit reached
    };
    this.recaptchaRef = createRef();
    this.forgotRecaptchaRef = createRef();  // captcha ref for send-otp screen
    this.resendRecaptchaRef = createRef();  // captcha ref for resend on step 2
  }

  componentDidMount() {
    this.tokenLogin();
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props && this.props.error) {
      // Reset reCAPTCHA on login failure
      if (this.recaptchaRef.current) {
        this.recaptchaRef.current.reset();
        this.setState({ recaptchaToken: null });
      }
      this.setAlert(this.props.errmsg || "Invalid login credentials!", "error");
    }
  }

  handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "otp") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      this.setState({ otp: digitsOnly });
      return;
    }
    this.setState({ [name]: value });
  };

  closeSnackbar = () => {
    this.setState({ showSnackbar: false });
  };

  setAlert = (msg, severity) => {
    this.setState({
      alertMsg: msg,
      alertSeverity: severity,
      showSnackbar: true,
    });
  };

  handleRecaptchaChange = (token) => {
    this.setState({ recaptchaToken: token });
  };

  handleRecaptchaExpired = () => {
    this.setState({ recaptchaToken: null });
  };

  tokenLogin() {
    const search = this.props.location.search;
    const params = new URLSearchParams(search);
    const token = params.get("token");
    if (!token) return;

    this.props.loginWithToken(token);
  }

  onVerifyOtp = () => {
    const { twoFAData } = this.props;
    if (!twoFAData?.userName) return;
    this.props.verifyLoginOtp(twoFAData.userName, this.state.otp);
  };

  onBackToLogin = () => {
    this.setState({ otp: "" });
    this.props.clear2FA();
  };

  // ─── Forgot Password Handlers ────────────────────────────────────────────

  // Step 1: send OTP to registered email/phone.
  onSendOtp = async () => {
    const { forgotUsername, forgotRecaptchaToken } = this.state;

    if (!forgotUsername.trim()) {
      this.setAlert("Please enter your username.", "error");
      return;
    }
    if (!forgotRecaptchaToken && !TENANT_ID) {
      this.setAlert("Please complete the reCAPTCHA verification.", "error");
      return;
    }

    this.setState({ forgotLoading: true });

    try {
      const sendOtpBody = { username: forgotUsername.trim() };
      if (forgotRecaptchaToken) sendOtpBody.captchaToken = forgotRecaptchaToken;
      const res = await axios.post(`${url}/auth/forgot-password/send-otp`, sendOtpBody);
      // API always returns 200 regardless of whether user exists (enumeration protection).
      this.setState({
        forgotStep: 2,
        forgotMaskedEmail: res.data.delivery?.email || "",
        forgotMaskedPhone: res.data.delivery?.phone || "",
        forgotOtp: "",
        forgotLoading: false,
        forgotRecaptchaToken: null,
        resendCountdown: 60,
      });
      if (this.forgotRecaptchaRef.current) this.forgotRecaptchaRef.current.reset();
      this._startResendCountdown();
    } catch (err) {
      this.setState({ forgotLoading: false });
      if (this.forgotRecaptchaRef.current) this.forgotRecaptchaRef.current.reset();
      this.setState({ forgotRecaptchaToken: null });
      const name = err.response?.data?.name;
      const status = err.response?.status;
      if (name === "CAPTCHA_FAILED") {
        this.setAlert("CAPTCHA verification failed. Please try again.", "error");
      } else if (status === 429) {
        this.setAlert(err.response.data?.message || "Too many OTP requests. Try again tomorrow.", "error");
      } else {
        this.setAlert("Something went wrong. Please try again.", "error");
      }
    }
  };

  // Step 2 (resend): reuses /send-otp. Captcha required in multi-tenant mode.
  onResendOtp = async () => {
    const { forgotUsername, resendRecaptchaToken } = this.state;

    if (!resendRecaptchaToken && !TENANT_ID) {
      this.setAlert("Please complete the reCAPTCHA verification to resend.", "error");
      return;
    }

    this.setState({ forgotLoading: true });
    try {
      const body = { username: forgotUsername };
      if (resendRecaptchaToken) body.captchaToken = resendRecaptchaToken;
      const res = await axios.post(`${url}/auth/forgot-password/send-otp`, body);
      const remaining = res.data.resendAttemptsRemaining ?? null;
      this.setState({
        forgotMaskedEmail: res.data.delivery?.email || "",
        forgotMaskedPhone: res.data.delivery?.phone || "",
        forgotOtp: "",
        forgotLoading: false,
        resendCountdown: 60,
        resendAttemptsRemaining: remaining,
        resendRecaptchaToken: null,
      });
      if (this.resendRecaptchaRef.current) this.resendRecaptchaRef.current.reset();
      this._startResendCountdown();
    } catch (err) {
      this.setState({ forgotLoading: false, resendRecaptchaToken: null });
      if (this.resendRecaptchaRef.current) this.resendRecaptchaRef.current.reset();
      const name = err.response?.data?.name;
      const status = err.response?.status;
      if (name === "CAPTCHA_FAILED") {
        this.setAlert("CAPTCHA verification failed. Please try again.", "error");
      } else if (status === 429) {
        this.setState({ resendAttemptsRemaining: 0 });
        this.setAlert(err.response.data?.message || "Daily OTP limit reached. Please try again tomorrow.", "error");
      } else {
        this.setAlert("Something went wrong. Please try again.", "error");
      }
    }
  };

  // countdown timer for resend OTP button on step 2
  _startResendCountdown = () => {
    if (this._resendTimer) clearInterval(this._resendTimer);
    this._resendTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendCountdown <= 1) {
          clearInterval(this._resendTimer);
          return { resendCountdown: 0 };
        }
        return { resendCountdown: prev.resendCountdown - 1 };
      });
    }, 1000);
  };

  // Step 2: verify the 6-digit OTP entered by the user
  onVerifyForgotOtp = async () => {
    const { forgotUsername, forgotOtp } = this.state;

    if (!forgotOtp || forgotOtp.trim().length === 0) {
      this.setAlert("Please enter the 6-digit code.", "error");
      return;
    }
    if (forgotOtp.trim().length !== 6) {
      this.setAlert("The code must be exactly 6 digits.", "error");
      return;
    }

    this.setState({ forgotLoading: true });
    try {
      const res = await axios.post(
        `${url}/auth/forgot-password/verify-otp`,
        { username: forgotUsername, otp: forgotOtp }
      );
      this.setState({ forgotStep: 3, forgotLoading: false, resetToken: res.data.resetToken || "" });
    } catch (err) {
      const name = err.response?.data?.name;
      const status = err.response?.status;
      this.setState({ forgotLoading: false });
      if (name === "OTP_LOCKED" || status === 429) {
        // All 5 attempts used — must restart from step 1
        if (this._resendTimer) clearInterval(this._resendTimer);
        this.setState({ forgotStep: 1, forgotOtp: "", resendCountdown: 0, resendAttemptsRemaining: null });
        this.setAlert("Too many incorrect attempts. Please request a new OTP.", "error");
      } else {
        this.setAlert("Incorrect OTP. Please try again.", "error");
      }
    }
  };

  // Step 3: submit new password
  onResetPassword = async () => {
    const { resetToken, newPassword, confirmPassword } = this.state;

    const pwdError = this._passwordStrengthError(newPassword);
    if (pwdError) {
      this.setState({ passwordError: pwdError });
      return;
    }
    if (newPassword !== confirmPassword) {
      this.setState({ confirmPasswordError: "Passwords do not match." });
      return;
    }

    this.setState({ forgotLoading: true });
    try {
      await axios.post(
        `${url}/auth/forgot-password/reset`,
        { resetToken, password: newPassword, confirmPassword }
      );
      this.setState({
        forgotStep: 0,
        forgotUsername: "",
        forgotOtp: "",
        newPassword: "",
        confirmPassword: "",
        passwordError: "",
        confirmPasswordError: "",
        forgotMaskedEmail: "",
        forgotMaskedPhone: "",
        forgotLoading: false,
        resetToken: "",
        userName: "",
        password: "",
      });
      this.setAlert("Password updated. Please sign in.", "success");
    } catch (err) {
      this.setState({ forgotLoading: false });
      const name = err.response?.data?.name;
      const status = err.response?.status;
      if (name === "INVALID_RESET_TOKEN" || status === 401) {
        // Token expired or already used — must restart from step 1
        this.setState({ forgotStep: 1, newPassword: "", confirmPassword: "", passwordError: "", confirmPasswordError: "", resetToken: "" });
        this.setAlert("Your reset session expired. Please request a new OTP.", "error");
      } else if (name === "ERR102") {
        const backendMsg = err.response?.data?.message || "";
        if (backendMsg === "PASSWORD_WEAK") {
          this.setAlert("Password must be 8–64 characters and include at least one number.", "error");
        } else if (backendMsg === "PASSWORD_MISMATCH") {
          this.setAlert("Passwords do not match.", "error");
        } else {
          this.setAlert("Invalid input. Please check your entries.", "error");
        }
      } else {
        this.setAlert("Reset failed. Please try again.", "error");
      }
    }
  };

  _passwordStrengthError = (pwd) => {
    if (!pwd) return "Please enter a new password.";
    if (pwd.length < 8) return "At least 8 characters required.";
    if (pwd.length > 64) return "Maximum 64 characters allowed.";
    if (!/\d/.test(pwd)) return "At least one number required.";
    return "";
  };

  onSubmit = async () => {
    const { recaptchaToken } = this.state;

    // Skip reCAPTCHA validation if TENANT_ID exists
    if (!TENANT_ID && !recaptchaToken) {
      this.setAlert("Please complete the reCAPTCHA verification", "error");
      return;
    }

    this.props.loginUser(this.state.userName, this.state.password, recaptchaToken);
  };

  render() {
    const { userName, password, otp, forgotStep } = this.state;
    const { isAuthenticated, requires2FA, twoFAData, isLoading } = this.props;

    if (isAuthenticated) {
      return <Redirect to="/" />;
    }

    // forgot password flow takes priority over everything except isAuthenticated
    if (forgotStep === 3) return this.renderResetPasswordScreen();
    if (forgotStep === 2) return this.renderForgotOtpScreen();
    if (forgotStep === 1) return this.renderSendOtpScreen();

    // existing login 2FA flow
    if (requires2FA && twoFAData) {
      return this.renderOtpScreen(twoFAData, otp, isLoading);
    }

    return this.renderLoginForm(userName, password);
  }

  // ─── Step 3: Enter new password + confirm → reset ────────────────────────
  renderResetPasswordScreen() {
    const {
      newPassword,
      confirmPassword,
      passwordError,
      confirmPasswordError,
      forgotLoading,
    } = this.state;

    const isResetDisabled = forgotLoading || !newPassword || !confirmPassword;

    return (
      <div>
        <Grid
          container
          style={{
            height: "100vh",
            position: "relative",
            background: `url(${login})`,
            backgroundSize: "cover",
            margin: "0px",
            padding: "0px",
          }}
        >
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100vh",
              width: "60vh",
            }}
          >
            <Box
              style={{
                display: "flex",
                overflow: "hidden",
                flexBasis: "100%",
                flexDirection: "column",
                margin: "auto",
                alignItems: "center",
                justifyContent: "center",
                width: "75%",
                height: "60%",
                borderStyle: "solid",
                borderColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                zIndex: 100,
              }}
            >
              <Typography style={{ marginTop: "56px", fontSize: 20, fontWeight: 600 }}>
                Partner Dashboard
              </Typography>
              <Box
                style={{
                  width: "80%",
                  height: "100%",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThemeProvider theme={theme}>
                  <Typography
                    variant="h6"
                    style={{ textAlign: "center", color: "black", marginBottom: "8px" }}
                  >
                    Set new password
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ textAlign: "center", color: "#616161", marginBottom: "20px" }}
                  >
                    Choose a strong password for your account.
                  </Typography>

                  {/* New password field */}
                  <TextField
                    onChange={(e) => this.setState({ newPassword: e.target.value, passwordError: "" })}
                    onBlur={() => {
                      const err = this._passwordStrengthError(newPassword);
                      if (err) this.setState({ passwordError: err });
                    }}
                    name="newPassword"
                    type="password"
                    placeholder="New Password"
                    variant="standard"
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    style={{
                      marginBottom: "4px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                    value={newPassword}
                  />
                  {passwordError ? (
                    <Typography style={{ width: "calc(100% - 16px)", fontSize: "0.75rem", color: "#c62828", marginBottom: "12px" }}>
                      {passwordError}
                    </Typography>
                  ) : <Box style={{ marginBottom: "12px" }} />}

                  {/* Confirm password field */}
                  <TextField
                    onChange={(e) => this.setState({ confirmPassword: e.target.value, confirmPasswordError: "" })}
                    onBlur={() => {
                      if (confirmPassword && confirmPassword !== newPassword) {
                        this.setState({ confirmPasswordError: "Passwords do not match." });
                      }
                    }}
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    variant="standard"
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    style={{
                      marginBottom: "4px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                    value={confirmPassword}
                    onKeyUp={(e) => { if (e.key === "Enter" && !isResetDisabled) this.onResetPassword(); }}
                  />
                  {confirmPasswordError ? (
                    <Typography style={{ width: "calc(100% - 16px)", fontSize: "0.75rem", color: "#c62828", marginBottom: "12px" }}>
                      {confirmPasswordError}
                    </Typography>
                  ) : <Box style={{ marginBottom: "12px" }} />}

                  <Button
                    fullWidth
                    variant="contained"
                    className="primary-action-btn"
                    disabled={isResetDisabled}
                    style={{
                      marginBottom: "8px",
                      backgroundColor: "#461486",
                      color: "white",
                    }}
                    onClick={this.onResetPassword}
                  >
                    {forgotLoading ? "Resetting…" : "Reset Password"}
                  </Button>

                  <Button
                    variant="text"
                    className="text-link-btn"
                    disableRipple
                    style={{ color: "#757575" }}
                    onClick={() =>
                      this.setState({
                        forgotStep: 2,
                        newPassword: "",
                        confirmPassword: "",
                        passwordError: "",
                        confirmPasswordError: "",
                      })
                    }
                  >
                    Back
                  </Button>
                </ThemeProvider>
              </Box>
            </Box>
          </Container>
        </Grid>
        <Toast
          open={this.state.showSnackbar}
          closeSnackbar={this.closeSnackbar}
          severity={this.state.alertSeverity}
          msg={this.state.alertMsg}
        />
      </div>
    );
  }

  // ─── Step 2: Enter 6-digit OTP sent to email/phone ───────────────────────
  renderForgotOtpScreen() {
    const {
      forgotOtp,
      forgotLoading,
      forgotMaskedEmail,
      forgotMaskedPhone,
      resendCountdown,
      resendAttemptsRemaining,
      resendRecaptchaToken,
    } = this.state;


    // Guard against the API returning the string "null" (e.g. "****null") when
    // a contact field is not set — filter it out alongside actual null/undefined/"".
    const isMeaningful = (v) => v && v !== "null" && !v.toLowerCase().includes("null");

    const maskedEmailClean = isMeaningful(forgotMaskedEmail) ? forgotMaskedEmail : null;
    const maskedPhoneClean = isMeaningful(forgotMaskedPhone) ? forgotMaskedPhone : null;

    const deliveryParts = [maskedEmailClean, maskedPhoneClean].filter(Boolean);

    const deliveryText = deliveryParts.length > 0 ? deliveryParts.join(" and ") : null;

    return (
      <div>
        <Grid
          container
          style={{
            height: "100vh",
            position: "relative",
            background: `url(${login})`,
            backgroundSize: "cover",
            margin: "0px",
            padding: "0px",
          }}
        >
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100vh",
              width: "60vh",
            }}
          >
            <Box
              style={{
                display: "flex",
                overflow: "hidden",
                flexBasis: "100%",
                flexDirection: "column",
                margin: "auto",
                alignItems: "center",
                justifyContent: "center",
                width: "75%",
                height: "60%",
                borderStyle: "solid",
                borderColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                zIndex: 100,
              }}
            >
              <Typography style={{ marginTop: "56px", fontSize: 20, fontWeight: 600 }}>
                Partner Dashboard
              </Typography>
              <Box
                style={{
                  width: "80%",
                  height: "100%",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThemeProvider theme={theme}>
                  <Typography
                    variant="h6"
                    style={{ textAlign: "center", color: "black", marginBottom: "8px" }}
                  >
                    Enter verification code
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ textAlign: "center", color: "#616161", marginBottom: "24px" }}
                  >
                    {deliveryText
                      ? <>We sent a 6-digit code to <strong>{deliveryText}</strong>. Enter it below.</>
                      : "If this account exists, a 6-digit code has been sent. Enter it below."}
                  </Typography>

                  {/* 6-digit OTP field — same validation as existing 2FA screen */}
                  <TextField
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                      this.setState({ forgotOtp: digitsOnly });
                    }}
                    name="forgotOtp"
                    placeholder="Enter 6-digit code"
                    variant="standard"
                    fullWidth
                    inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                    InputProps={{ disableUnderline: true }}
                    style={{
                      marginBottom: "20px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                    value={forgotOtp}
                    onKeyUp={(e) => { if (e.key === "Enter") this.onVerifyForgotOtp(); }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    className="primary-action-btn"
                    disabled={forgotLoading || forgotOtp.length !== 6}
                    style={{
                      marginBottom: "12px",
                      backgroundColor: "#461486",
                      color: "white",
                    }}
                    onClick={this.onVerifyForgotOtp}
                  >
                    {forgotLoading ? "Verifying…" : "Verify OTP"}
                  </Button>

                  {/* S2: countdown — plain text while timer runs, not a button */}
                  {resendCountdown > 0 && (
                    <Typography style={{ fontSize: "0.8rem", color: "#9e9e9e", marginBottom: "4px" }}>
                      Resend OTP in {resendCountdown}s
                    </Typography>
                  )}

                  {/* S2: once countdown ends — show captcha (multi-tenant) then the resend button */}
                  {resendCountdown === 0 && (
                    <>
                      {!TENANT_ID && resendAttemptsRemaining !== 0 && (
                        <Box style={{ marginBottom: "12px", display: "flex", justifyContent: "center" }}>
                          <ReCAPTCHA
                            ref={this.resendRecaptchaRef}
                            sitekey={GOOGLE_PUBLIC_KEY}
                            onChange={(token) => this.setState({ resendRecaptchaToken: token })}
                            onExpired={() => this.setState({ resendRecaptchaToken: null })}
                          />
                        </Box>
                      )}
                      {resendAttemptsRemaining === 0 ? (
                        <Typography style={{ fontSize: "0.8rem", color: "#9e9e9e", marginBottom: "4px" }}>
                          No more resends available
                        </Typography>
                      ) : (
                        <Button
                          variant="text"
                          className="text-link-btn"
                          disableRipple
                          disabled={forgotLoading}
                          style={{ color: "#461486", marginBottom: "4px" }}
                          onClick={this.onResendOtp}
                        >
                          Resend OTP
                        </Button>
                      )}
                    </>
                  )}

                  <Button
                    variant="text"
                    className="text-link-btn"
                    disableRipple
                    style={{ color: "#757575" }}
                    onClick={() => {
                      if (this._resendTimer) clearInterval(this._resendTimer);
                      this.setState({
                        forgotStep: 1,
                        forgotOtp: "",
                        resendCountdown: 0,
                        resendAttemptsRemaining: null,
                        forgotRecaptchaToken: null,
                        resendRecaptchaToken: null,
                      });
                    }}
                  >
                    Back
                  </Button>
                </ThemeProvider>
              </Box>
            </Box>
          </Container>
        </Grid>
        <Toast
          open={this.state.showSnackbar}
          closeSnackbar={this.closeSnackbar}
          severity={this.state.alertSeverity}
          msg={this.state.alertMsg}
        />
      </div>
    );
  }

  // ─── Step 1: Enter username + reCAPTCHA → triggers OTP delivery ──────────
  renderSendOtpScreen() {
    const { forgotUsername, forgotLoading, forgotRecaptchaToken } = this.state;

    const isSendDisabled = forgotLoading
      || !forgotUsername.trim()
      || (!forgotRecaptchaToken && !TENANT_ID);

    return (
      <div>
        <Grid
          container
          style={{
            height: "100vh",
            position: "relative",
            background: `url(${login})`,
            backgroundSize: "cover",
            margin: "0px",
            padding: "0px",
          }}
        >
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100vh",
              width: "60vh",
            }}
          >
            <Box
              style={{
                display: "flex",
                overflow: "hidden",
                flexBasis: "100%",
                flexDirection: "column",
                margin: "auto",
                alignItems: "center",
                justifyContent: "center",
                width: "75%",
                height: "60%",
                borderStyle: "solid",
                borderColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                zIndex: 100,
              }}
            >
              <Typography style={{ paddingTop: "56px", fontSize: 20, fontWeight: 600 }}>
                Partner Dashboard
              </Typography>
              <Box
                style={{
                  width: "80%",
                  height: "100%",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThemeProvider theme={theme}>
                  <Typography
                    variant="h6"
                    style={{ textAlign: "center", color: "black", marginBottom: "8px" }}
                  >
                    Forgot Password
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ textAlign: "center", color: "#616161", marginBottom: "24px" }}
                  >
                    Enter your username. We&apos;ll send a one-time code to your
                    registered email and phone.
                  </Typography>

                  {/* Username / email field */}
                  <TextField
                    onChange={(e) => this.setState({ forgotUsername: e.target.value })}
                    name="forgotUsername"
                    placeholder="Username"
                    variant="standard"
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    style={{
                      marginBottom: "20px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                    value={forgotUsername}
                    onKeyUp={(e) => { if (e.key === "Enter") this.onSendOtp(); }}
                  />

                  {/* reCAPTCHA — skipped when TENANT_ID is set (matches login behaviour, M2/M9) */}
                  {!TENANT_ID && (
                    <Box style={{ marginBottom: "20px", display: "flex", justifyContent: "center" }}>
                      <ReCAPTCHA
                        ref={this.forgotRecaptchaRef}
                        sitekey={GOOGLE_PUBLIC_KEY}
                        onChange={(token) => this.setState({ forgotRecaptchaToken: token })}
                        onExpired={() => this.setState({ forgotRecaptchaToken: null })}
                      />
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    className="primary-action-btn"
                    disabled={isSendDisabled}
                    style={{
                      marginBottom: "12px",
                      backgroundColor: "#461486",
                      color: "white",
                    }}
                    onClick={this.onSendOtp}
                  >
                    {forgotLoading ? "Sending…" : "Send OTP"}
                  </Button>

                  <Button
                    variant="text"
                    className="text-link-btn"
                    disableRipple
                    style={{ color: "#757575" }}
                    onClick={() =>
                      this.setState({
                        forgotStep: 0,
                        forgotUsername: "",
                        forgotRecaptchaToken: null,
                      })
                    }
                  >
                    Back to Sign In
                  </Button>
                </ThemeProvider>
              </Box>
            </Box>
          </Container>
        </Grid>
        <Toast
          open={this.state.showSnackbar}
          closeSnackbar={this.closeSnackbar}
          severity={this.state.alertSeverity}
          msg={this.state.alertMsg}
        />
      </div>
    );
  }

  renderOtpScreen(twoFAData, otp, isLoading) {
    const { maskedEmail, maskedPhone } = twoFAData;
    const deliveryText = [maskedEmail, maskedPhone].filter(Boolean).join(" and ") || "your email and phone";
    return (
      <div>
        <Grid
          container
          style={{
            height: "100vh",
            position: "relative",
            background: `url(${login})`,
            backgroundSize: "cover",
            margin: "0px",
            padding: "0px",
          }}
        >
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100vh",
              width: "60vh",
            }}
          >
            <Box
              style={{
                display: "flex",
                overflow: "hidden",
                flexBasis: "100%",
                flexDirection: "column",
                margin: "auto",
                alignItems: "center",
                justifyContent: "center",
                width: "75%",
                height: "60%",
                borderStyle: "solid",
                borderColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                zIndex: 100,
              }}
            >
              <Typography
                style={{
                  marginTop: "56px",
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                Partner Dashboard
              </Typography>
              <Box
                style={{
                  width: "80%",
                  height: "100%",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThemeProvider theme={theme}>
                  <Typography
                    variant="h6"
                    style={{
                      textAlign: "center",
                      color: "black",
                      marginBottom: "12px",
                    }}
                  >
                    Verify your identity
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      textAlign: "center",
                      color: "#616161",
                      marginBottom: "24px",
                    }}
                  >
                    We sent a 6-digit code to {deliveryText}. Enter it below.
                    <br />
                    Code expired? Click &quot;Back to sign in&quot; to receive a new one.
                  </Typography>
                  <TextField
                    onChange={this.handleChange}
                    name="otp"
                    placeholder="Enter 6-digit code"
                    variant="standard"
                    fullWidth
                    inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    style={{
                      marginBottom: "20px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                    value={otp}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") this.onVerifyOtp();
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    className="primary-action-btn"
                    disabled={isLoading || (otp || "").trim().length !== 6}
                    style={{
                      marginBottom: "12px",
                      border: "#a0a0a0",
                      backgroundColor: "#461486",
                      color: "white",
                    }}
                    onClick={this.onVerifyOtp}
                  >
                    {isLoading ? "Verifying…" : "Verify"}
                  </Button>
                  <Button
                    variant="text"
                    className="text-link-btn"
                    disableRipple
                    style={{ color: "#757575" }}
                    onClick={this.onBackToLogin}
                  >
                    Back to sign in
                  </Button>
                </ThemeProvider>
              </Box>
            </Box>
          </Container>
        </Grid>
        <Toast
          open={this.state.showSnackbar}
          closeSnackbar={this.closeSnackbar}
          severity={this.state.alertSeverity}
          msg={this.state.alertMsg}
        />
      </div>
    );
  }

  renderLoginForm(userName, password) {
    return (
      <div>
        <Grid
          container
          style={{
            height: "100vh",
            position: "relative",
            background: `url(${login})`,
            backgroundSize: "cover",
            margin: "0px",
            padding: "0px",
          }}
        >
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100vh",
              width: "60vh",
            }}
          >
            <Box
              style={{
                display: "flex",
                overflow: "hidden",
                flexBasis: "100%",
                flexDirection: "column",
                margin: "auto",
                alignItems: "center",
                justifyContent: "center",
                width: "75%",
                height: "60%",
                borderStyle: "solid",
                borderColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                zIndex: 100,
              }}
            >
              <Typography
                style={{
                  marginTop: "56px",
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                Partner Dashboard
              </Typography>
              <Box
                style={{
                  width: "80%",
                  height: "100%",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThemeProvider theme={theme}>
                  <Typography
                    variant="h6"
                    style={{
                      textAlign: "center",
                      color: "black",
                      marginBottom: "20px",
                    }}
                  >
                    Sign In
                  </Typography>
                  <TextField
                    onChange={this.handleChange}
                    name="userName"
                    placeholder="Username"
                    variant="standard"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                    }}
                    style={{
                      marginBottom: "20px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                  />
                  <TextField
                    onChange={this.handleChange}
                    variant="standard"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                    }}
                    name="password"
                    type="password"
                    value={this.state.password}
                    placeholder="Password"
                    onKeyUp={(event) => {
                      if (event.key == "Enter") this.onSubmit();
                    }}
                    style={{
                      marginBottom: "20px",
                      backgroundColor: "#F1F1F1",
                      borderRadius: "8px",
                      width: "calc(100% - 16px)",
                      padding: "8px",
                    }}
                  />

                  {!TENANT_ID && (
                    <Box style={{ marginBottom: "20px", display: "flex", justifyContent: "center" }}>
                      <ReCAPTCHA
                        ref={this.recaptchaRef}
                        sitekey={GOOGLE_PUBLIC_KEY}
                        onChange={this.handleRecaptchaChange}
                        onExpired={this.handleRecaptchaExpired}
                      />
                    </Box>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    style={{
                      marginBottom: "8px",
                      border: "#a0a0a0",
                      backgroundColor: "#461486",
                      color: "white",
                    }}
                    onClick={() => {
                      this.onSubmit();
                    }}
                  >
                    Continue
                  </Button>
                  <Button
                    variant="text"
                    className="text-link-btn"
                    disableRipple
                    style={{
                      marginBottom: "12px",
                      color: "#461486",
                      textTransform: "none",
                      fontSize: "0.85rem",
                    }}
                    onClick={() => this.setState({ forgotStep: 1 })}
                  >
                    Forgot password?
                  </Button>
                </ThemeProvider>
              </Box>
            </Box>
          </Container>
        </Grid>

        <Toast
          open={this.state.showSnackbar}
          closeSnackbar={this.closeSnackbar}
          severity={this.state.alertSeverity}
          msg={this.state.alertMsg}
        />
      </div>
    );
  }
}

function Signup() {
  return (
    <>
      <Typography
        variant="subtitle1"
        style={{
          marginTop: "0px",
          textAlign: "center",
          color: "black",
        }}
      >
        OR{" "}
      </Typography>
      <Button
        type="submit"
        fullWidth
        variant="outlined"
        style={{
          color: "gray",
          top: "2%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: "#757575",
          backgroundColor: "#ffffff",
          borderRadius: "0%",
          width: "90%",
          height: "50px",
          boxShadow: "3px 3px #F1F1F1",
          marginTop: "20px",
        }}
      >
        Continue With Google
      </Button>
      <Box className=" sign1" align="center">
        <a href="/signup">
          <span
            className="root body1 ColorPrimary align"
            style={{ paddingLeft: "2px" }}
          >
            Sign up for new account
          </span>
        </a>
      </Box>
    </>
  );
}
const theme = createTheme({
  palette: {
    gray: {
      main: "#bdbdbd",
      darker: "#616161",
    },
    black: {
      main: "#212121",
      contrastText: "#424242",
    },
  },
});

export default connect(
  (state) => ({
    isAuthenticated: state.login.isAuthenticated,
    isLoading: state.login.isLoading,
    error: state.login.error,
    errmsg: state.login.errmsg,
    requires2FA: state.login.requires2FA,
    twoFAData: state.login.twoFAData,
  }),
  { loginUser, loginWithToken, verifyLoginOtp, clear2FA },
)(Login);
